<template>
  <button
    type="button"
    class="hub-option-card"
    :class="{
      'hub-option-card--cta': !!ctaLabel,
      'hub-option-card--banner': isBanner,
      'hub-option-card--new': isNew,
      'hub-option-card--timed': !!badge
    }"
    @click="$emit('click')"
  >
    <div class="hub-option-card__media">
      <picture>
        <source
          v-if="imageMobile"
          media="(max-width: 1023px)"
          :srcset="imageMobile"
        />
        <img
          :src="image"
          :alt="title"
          class="hub-option-card__image"
        />
      </picture>
      <div
        v-if="isBanner"
        class="hub-option-card__overlay"
      >
        <span class="hub-option-card__icon">
          <img
            v-if="iconSrc"
            :src="iconSrc"
            alt=""
            class="hub-option-card__icon-img"
          />
          <OmtIcons
            v-else
            :name="icon"
            size="1x"
            color="#FFFFFF"
          />
        </span>
        <OmtTypography
          as="h2-bold"
          class="hub-option-card__title hub-option-card__title--on-image"
        >
          {{ title }}
        </OmtTypography>
      </div>
      <span
        v-else
        class="hub-option-card__icon"
      >
        <img
          v-if="iconSrc"
          :src="iconSrc"
          alt=""
          class="hub-option-card__icon-img"
        />
        <OmtIcons
          v-else
          :name="icon"
          size="1x"
          color="var(--colors-light-primary-navy-blue)"
        />
      </span>
      <HubNewBadge
        v-if="isNew"
        class="hub-option-card__badge"
      />
      <span
        v-if="badge"
        class="hub-option-card__time"
        >{{ badge }}</span
      >
    </div>
    <div class="hub-option-card__body">
      <div
        v-if="!isBanner"
        class="hub-option-card__header"
      >
        <OmtTypography
          as="h2-bold"
          class="hub-option-card__title"
          >{{ title }}</OmtTypography
        >
        <OmtIcons
          name="chevron-right"
          size="sm"
          color="var(--colors-light-primary-gray1)"
          class="hub-option-card__chevron"
        />
      </div>
      <div class="hub-option-card__content">
        <OmtTypography
          as="p2"
          class="hub-option-card__description"
          >{{ description }}</OmtTypography
        >
        <OmtIcons
          v-if="isBanner"
          name="chevron-right"
          size="sm"
          color="var(--colors-light-primary-gray1)"
          class="hub-option-card__chevron"
        />
      </div>
      <OmtTypography
        v-if="ctaLabel"
        as="l2"
        class="hub-option-card__cta"
      >
        {{ ctaLabel }}
        <OmtIcons
          name="chevron-right"
          size="xs"
          color="var(--colors-light-primary-navy-blue)"
        />
      </OmtTypography>
    </div>
  </button>
</template>

<script>
import HubNewBadge from "./HubNewBadge.vue";

export default {
  name: "HubOptionCard",
  components: { HubNewBadge },
  props: {
    title: { type: String, required: true },
    description: { type: String, required: true },
    image: { type: String, required: true },
    imageMobile: { type: String, default: "" },
    icon: { type: String, required: true },
    isNew: { type: Boolean, default: false },
    badge: { type: String, default: "" },
    ctaLabel: { type: String, default: "" },
    variant: { type: String, default: "default" }
  },
  computed: {
    isBanner() {
      return this.variant === "banner";
    },
    iconSrc() {
      const icon = this.icon || "";
      if (!icon.includes("/")) return "";
      const base = process.env.VUE_APP_BASE_URL || "/";
      return `${base}${icon.replace(/^\//, "")}`;
    }
  }
};
</script>

<style scoped>
.hub-option-card {
  display: flex;
  width: 100%;
  height: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid var(--colors-light-secondary-divider);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  min-height: 108px;
}

.hub-option-card__media {
  position: relative;
  flex: 0 0 112px;
  min-height: 108px;
  overflow: hidden;
}

.hub-option-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hub-option-card__icon {
  position: absolute;
  left: 8px;
  bottom: 8px;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: #ffffff33;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
}

.hub-option-card__icon-img {
  width: 16px;
  height: 16px;
  display: block;
}

.hub-option-card__badge {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 3;
}

.hub-option-card__time {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  background: var(--colors-light-support-news);
  color: #fff;
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
}

.hub-option-card__body {
  flex: 1;
  padding: 12px 16px 12px 12px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hub-option-card__header,
.hub-option-card__content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.hub-option-card__title {
  color: var(--colors-light-primary-navy-blue);
}

.hub-option-card__description {
  margin-top: 6px;
  color: var(--colors-light-primary-gray1);
}

.hub-option-card__cta {
  margin-top: auto;
  padding-top: 12px;
  color: var(--colors-light-primary-navy-blue);
  display: none;
  align-items: center;
  gap: 4px;
}

.hub-option-card--banner {
  flex-direction: column;
  min-height: unset;
}

.hub-option-card--banner .hub-option-card__media {
  flex: 0 0 63px;
  width: 100%;
  height: 63px;
  min-height: 63px;
  max-height: 63px;
}

.hub-option-card--banner .hub-option-card__media::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(
    90deg,
    rgba(16, 24, 48, 0.42) 0%,
    rgba(16, 24, 48, 0.18) 48%,
    rgba(16, 24, 48, 0.08) 100%
  );
}

.hub-option-card--banner .hub-option-card__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
}

.hub-option-card--new.hub-option-card--banner .hub-option-card__overlay,
.hub-option-card--timed.hub-option-card--banner .hub-option-card__overlay {
  padding-right: 88px;
}

.hub-option-card--banner .hub-option-card__icon {
  position: static;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: #ffffff33;
  box-shadow: none;
}

.hub-option-card--banner .hub-option-card__title--on-image {
  color: #fff;
  margin: 0;
  min-width: 0;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hub-option-card--banner .hub-option-card__body {
  padding: 12px 16px;
  flex: 1;
}

.hub-option-card--banner .hub-option-card__content {
  align-items: center;
}

.hub-option-card--banner .hub-option-card__description {
  margin: 0;
  margin-top: 0;
}

.hub-option-card--banner .hub-option-card__description >>> * {
  margin: 0 !important;
}

.hub-option-card--banner .hub-option-card__badge,
.hub-option-card--banner .hub-option-card__time {
  top: 6px;
  right: 8px;
  z-index: 3;
}

@media screen and (min-width: 1024px) {
  .hub-option-card:not(.hub-option-card--banner) {
    flex-direction: column;
    min-height: 100%;
    height: 100%;
  }

  .hub-option-card:not(.hub-option-card--banner) .hub-option-card__media {
    flex: 0 0 140px;
    width: 100%;
    min-height: 140px;
  }

  .hub-option-card:not(.hub-option-card--banner) .hub-option-card__body {
    padding: 16px;
    flex: 1;
  }

  .hub-option-card--cta:not(.hub-option-card--banner) .hub-option-card__media {
    flex-basis: 168px;
    min-height: 168px;
  }

  .hub-option-card--cta .hub-option-card__cta {
    display: inline-flex;
  }

  .hub-option-card--cta:not(.hub-option-card--banner) .hub-option-card__chevron {
    display: none;
  }

  .hub-option-card--banner .hub-option-card__media {
    flex-basis: 63px;
    height: 63px;
    min-height: 63px;
    max-height: 63px;
  }
}
</style>
